/**
 * Role Switcher Component - Admin Only
 * Allows admins to temporarily impersonate other roles for testing RBAC
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePermissions, ROLE_LABELS, ROLE_STYLES } from '../hooks/usePermissions';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';
import { Eye, EyeOff, ChevronDown, Shield, Users, Briefcase, UserCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const IMPERSONATABLE_ROLES = [
  { value: 'medic', label: 'Medic', icon: UserCircle, description: 'Portal access only' },
  { value: 'ops_coordinator', label: 'Ops Coordinator', icon: Briefcase, description: 'Operations role' },
  { value: 'manager', label: 'Manager', icon: Users, description: 'Supervisory + Compliance' },
];

export function RoleSwitcher() {
  const { startImpersonation, stopImpersonation, isImpersonating, effectiveRole, user } = useAuth();
  const { isActualAdmin } = usePermissions();
  const [loading, setLoading] = useState(false);

  // Only show for actual admins
  if (!isActualAdmin) {
    return null;
  }

  const handleImpersonate = async (role) => {
    try {
      setLoading(true);
      // Reset the impersonation timer
      localStorage.setItem('impersonation_start', Date.now().toString());
      await startImpersonation(role);
      toast.success(`Now viewing as ${ROLE_LABELS[role] || role}`, {
        description: 'UI and permissions are now restricted to this role',
      });
      // Redirect to appropriate page based on role
      if (role === 'medic') {
        window.location.href = '/portal';
      } else {
        window.location.reload();
      }
    } catch (error) {
      toast.error('Failed to switch role', {
        description: error.response?.data?.detail || error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStopImpersonation = async () => {
    try {
      setLoading(true);
      localStorage.removeItem('impersonation_start');
      await stopImpersonation();
      toast.success('Returned to Admin view', {
        description: 'Full admin permissions restored',
      });
      // Reload page to restore all permissions
      window.location.reload();
    } catch (error) {
      toast.error('Failed to stop impersonation', {
        description: error.response?.data?.detail || error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  if (isImpersonating) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleStopImpersonation}
        disabled={loading}
        className="border-amber-500/50 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 hover:text-amber-300"
        data-testid="stop-impersonation-btn"
      >
        <EyeOff className="w-4 h-4 mr-2" />
        Exit {ROLE_LABELS[effectiveRole] || effectiveRole} View
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={loading}
          className="border-white/10 text-[#A1A1AA] hover:text-[#F2F2F2] hover:bg-white/5"
          data-testid="role-switcher-btn"
        >
          <Eye className="w-4 h-4 mr-2" />
          View As
          <ChevronDown className="w-3 h-3 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#2B2B2B] border-white/10 w-56">
        <DropdownMenuLabel className="text-[#A1A1AA] text-xs">
          Test RBAC as another role
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        {IMPERSONATABLE_ROLES.map((role) => {
          const Icon = role.icon;
          return (
            <DropdownMenuItem
              key={role.value}
              onClick={() => handleImpersonate(role.value)}
              className="text-[#F2F2F2] focus:bg-white/10 cursor-pointer"
              data-testid={`impersonate-${role.value}`}
            >
              <Icon className="w-4 h-4 mr-2 text-[#A1A1AA]" />
              <div className="flex-1">
                <p className="font-medium">{role.label}</p>
                <p className="text-xs text-[#A1A1AA]">{role.description}</p>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Impersonation Banner - Shows when admin is impersonating another role
 * Includes countdown timer for the 4-hour impersonation limit
 */
export function ImpersonationBanner() {
  const { isImpersonating, effectiveRole, stopImpersonation, user } = useAuth();
  const { isActualAdmin } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showWarning, setShowWarning] = useState(false);

  // Impersonation timeout is 4 hours (set in backend)
  const IMPERSONATION_TIMEOUT = 4 * 60 * 60 * 1000; // 4 hours in ms
  const WARNING_THRESHOLD = 15 * 60 * 1000; // 15 minutes warning

  useEffect(() => {
    if (!isImpersonating || !isActualAdmin) {
      setTimeRemaining(null);
      return;
    }

    // Get impersonation start time from localStorage or set it now
    let startTime = localStorage.getItem('impersonation_start');
    if (!startTime) {
      startTime = Date.now().toString();
      localStorage.setItem('impersonation_start', startTime);
    }

    const updateTimer = () => {
      const elapsed = Date.now() - parseInt(startTime);
      const remaining = IMPERSONATION_TIMEOUT - elapsed;
      
      if (remaining <= 0) {
        // Session expired - force stop
        localStorage.removeItem('impersonation_start');
        stopImpersonation().then(() => window.location.reload());
        return;
      }
      
      setTimeRemaining(remaining);
      setShowWarning(remaining <= WARNING_THRESHOLD);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [isImpersonating, isActualAdmin, stopImpersonation]);

  if (!isImpersonating || !isActualAdmin) {
    return null;
  }

  const handleStop = async () => {
    try {
      setLoading(true);
      localStorage.removeItem('impersonation_start');
      await stopImpersonation();
      window.location.reload();
    } catch (error) {
      toast.error('Failed to stop impersonation');
    } finally {
      setLoading(false);
    }
  };

  // Format time remaining
  const formatTime = (ms) => {
    if (!ms || ms <= 0) return '0:00';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      className={`border-b px-4 py-2 flex flex-col sm:flex-row sm:items-center justify-between gap-2 relative z-50 ${
        showWarning 
          ? 'bg-red-500/20 border-red-500/30' 
          : 'bg-amber-500/20 border-amber-500/30'
      }`}
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <Eye className={`w-4 h-4 ${showWarning ? 'text-red-400' : 'text-amber-400'}`} />
        <span className={`text-sm ${showWarning ? 'text-red-200' : 'text-amber-200'}`}>
          <strong>Impersonation Mode:</strong> Viewing as{' '}
          <Badge className={ROLE_STYLES[effectiveRole] || 'bg-gray-500/15 text-gray-400'}>
            {ROLE_LABELS[effectiveRole] || effectiveRole}
          </Badge>
        </span>
        <span className={`text-xs hidden sm:inline ${showWarning ? 'text-red-400/70' : 'text-amber-400/70'}`}>
          (Logged in as {user?.name} - Admin)
        </span>
      </div>
      <div className="flex items-center gap-3">
        {timeRemaining && (
          <div className={`flex items-center gap-1.5 text-xs ${
            showWarning ? 'text-red-300' : 'text-amber-300'
          }`}>
            <Clock className="w-3.5 h-3.5" />
            <span>
              {showWarning ? 'Expires in ' : 'Session: '}
              <strong>{formatTime(timeRemaining)}</strong>
            </span>
          </div>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={handleStop}
          disabled={loading}
          data-testid="banner-exit-impersonation-btn"
          className={`${
            showWarning 
              ? 'text-red-400 hover:text-red-300 hover:bg-red-500/20' 
              : 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/20'
          }`}
        >
          <EyeOff className="w-4 h-4 mr-1" />
          Exit
        </Button>
      </div>
    </div>
  );
}

export default RoleSwitcher;
